/** @module */
import events from "_src/engine/events";

import AbstractManipulator from "_src/client/manipulators/abstract_manipulator";
import windowManager from "_src/client/window_manager";
import renderGraph from "_src/render_graph/render_graph";
import workspace from "_src/workspace";
import history from "_src/history/manager";
import { stepType } from "_src/constants";
import appState from "_src/app_state";
import gizmo from "_src/client/controls/gizmo";
import {vec4, quat} from "_src/libs/gl-matrix";

const usableObjectsFilter = object => object.getVisibility() && !object.getLockState();

export default class TransformManipulator extends AbstractManipulator {

  constructor(scene) {

    super(scene.camera);

    this._loaded = false;
    this._isMoving = false;

    this._objectsOffsets = new WeakMap();

  }

  update() {

    gizmo.clearAttachedObjects();
    const models = workspace.getObjects().filter(usableObjectsFilter).map(object => object.model);
    for (const model of models) {
      gizmo.attachObject(model);
    }

    this.updatePosition();
    this.updateRotation();
  }

  updatePosition() {
    gizmo.setPosition(getCenterOfUnlockedObjects(gizmo.getPosition()));
  }

  updateRotation() {
    gizmo.setRotation(getAveragedRotationOfUnlockedObjects(gizmo.getRotation()));
  }

  _onStart() {

    this.update();
    gizmo.updateGizmo(this._camera);

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    mainRenderGraph.setEnabled(renderGraph.TRANSFORM, true);

  }

  _onStop() {
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    mainRenderGraph.setEnabled(renderGraph.TRANSFORM, false);
    gizmo.clearAttachedObjects();
  }

  _mouseMoveHandler({screenX, screenY, active}) {

    if (active) {

      if (!gizmo.isActive()) {
        return;
      }

      const objects = workspace.getObjects().filter(usableObjectsFilter);

      if (this._isMoving) {
        gizmo.transform(this._camera, screenX, screenY);

        for (const object of objects) {
          object.model.updateMatrix();
          object.move(object.model.modelMatrix);
          events.emit("shallow_object_update", object);
        }
      } else {
        gizmo.startMove(this._camera, screenX, screenY);
        appState.setStateValue("manipulatorIsActive", true);

        for (const object of objects) {
          history.writeObjectToCurrentStep(object);
        }

        this._isMoving = true;
      }
    } else {

      if (this._isMoving) {
        gizmo.stopMove();
        appState.setStateValue("manipulatorIsActive", false);
        this._isMoving = false;
      }

      gizmo.resetHighlight();
      gizmo.highlight(this._camera, screenX, screenY);
    }

    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame(active && this._isMoving);
  }

  _mouseDownHandler({screenX, screenY}) {
    gizmo.pick(this._camera, screenX, screenY);
  }

  _mouseUpHandler() {

    if (!gizmo.isActive()) {
      return;
    }

    gizmo.stopMove();
    gizmo.updateGizmo(this._camera);
    appState.setStateValue("manipulatorIsActive", false);

    const objects = workspace.getObjects().filter(usableObjectsFilter);
    if (objects.length) {
      for (const object of objects) {
        object.move(object.model.modelMatrix, true);
        events.emit("object_moved", object);
      }
      history.addStep(stepType.TRANSFORM);
      events.emit("objects_updated", objects);
    }

    this._isMoving = false;
  }

  _resizeHandler() {
    gizmo.updateGizmo(this._camera);
  }

  _cameraMoveHandler() {
    gizmo.updateGizmo(this._camera);
  }

  cleanup() {
    gizmo.cleanup();
  }

}

function getCenterOfUnlockedObjects(dest) {

  const bbox = workspace.getBBox(usableObjectsFilter);

  dest[0] = 0.5 * (bbox[0] + bbox[3]);
  dest[1] = 0.5 * (bbox[1] + bbox[4]);
  dest[2] = 0.5 * (bbox[2] + bbox[5]);

  return dest;

}

function getAveragedRotationOfUnlockedObjects(dest) {
  const objects = workspace.getObjects().filter(usableObjectsFilter);
  vec4.set(dest, 0,0,0,0);
  for (const object of objects) {
    vec4.add(dest, dest, object.model.getRotation());
  }
  quat.normalize(dest, dest);

  return dest;
}
